/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-01 15:00:00
 *
 *******************************************************/
import _ from 'lodash';

import React, { Component, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Card, Checkbox } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { reduxForm } from 'redux-form';
import { routes } from 'src/routes';

import history from 'src/routes/history';
import * as installActions from 'actions/install-actions';
import { DATA_PLUGIN } from 'src/constants/installation-v2';
import ButtonCustom from './button';
import { useEffect } from 'react';
import { checkNewUserSSR } from 'src/helpers/installation-helper';

type Props = WithI18nProps;

const SelectPlugin = withI18n()(({ values = [], handleSelect, i18n, onHover }) => {
  return (
    <div className="SelectPlugin">
      {DATA_PLUGIN.map((item, index) => {
        const { image, title, description, value, hover } = item;
        return (
          <div
            onMouseOver={() => onHover(hover)}
            key={index}
            className={`SelectPlugin__Item ${
              values?.includes(value) ? `SelectPlugin__Item--Selected` : ''
            }`}
            style={{ '--hover': hover }}
            onClick={() => handleSelect(!values?.includes(value), index, value)}>
            <Checkbox
              checked={values?.includes(value)}
              onChange={newChecked => handleSelect(newChecked, index, value)}
            />
            <div className="icon">
              <img src={image} alt="icon" />
            </div>
            <div className="title">{i18n.translate(title)}</div>
            <div className="description">{i18n.translate(description)}</div>
          </div>
        );
      })}
    </div>
  );
});

const KYCStep1 = withI18n()(({ onSubmitKYC, features, i18n, loading, isNewUser }) => {
  const [values, setValues] = useState(features);
  const [bgHover, setBgHover] = useState('/assets/images/installation/feature-filter-2x.png');

  useEffect(() => {
    setValues(features);
  }, [features]);

  const handleSelect = useCallback(
    (newChecked, index, value) => {
      if (newChecked) {
        setValues([...values, value]);
      } else {
        setValues(values.filter(key => key !== value));
      }
    },
    [values]
  );

  const onHover = bg => {
    setBgHover(bg);
  };

  return (
    <Card>
      <div className="BC-page_Installation--KYC">
        <div className="wrapper">
          <div className="content">
            <div className="StepForm">
              <div className="step-title">
                {i18n.translate('AdminHTML.InstallationV2.KYC.step1_title')}
              </div>
              <div className="step-description">
                {i18n.translate('AdminHTML.InstallationV2.KYC.step1_description')}
              </div>
              <div className="step-form">
                <SelectPlugin
                  values={values}
                  handleSelect={handleSelect}
                  onHover={onHover}></SelectPlugin>
              </div>
              <div className="menu">
                {values.length > 0 ? (
                  isNewUser ? (
                    <ButtonCustom loading={loading} onClick={() => onSubmitKYC(values, true)}>
                      {i18n.translate('AdminHTML.Common.Action.next')}
                    </ButtonCustom>
                  ) : (
                    <ButtonCustom loading={loading} onClick={() => onSubmitKYC(values)}>
                      {i18n.translate('AdminHTML.InstallationV2.KYC.step4_choose_theme')}
                    </ButtonCustom>
                  )
                ) : isNewUser ? (
                  <ButtonCustom isDisabled={true}>
                    {i18n.translate('AdminHTML.Common.Action.next')}
                  </ButtonCustom>
                ) : (
                  <ButtonCustom isDisabled={true}>
                    {i18n.translate('AdminHTML.InstallationV2.KYC.step4_choose_theme')}
                  </ButtonCustom>
                )}
              </div>
            </div>
          </div>
          <div className="preview">
            <img src={bgHover} alt="" />
          </div>
        </div>
      </div>
    </Card>
  );
});

class KYCScreen extends Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      currentStepIndex: 1,
      features: [],
      industries: [],
      revenue: null
    };
  }

  componentDidMount() {
    const { installStatus, kycData } = this.props;
    const features = _.get(kycData, 'features', []);
    const industries = _.get(kycData, 'industries', []);
    const revenue = _.get(kycData, 'revenue', null);
    const step = installStatus.split('submitted-kyc-')[1];

    this.setState({
      currentStepIndex: +step + 1 || 1,
      features,
      industries,
      revenue
    });
  }

  onSetStep = async (key, data) => {
    const { updateInstallationKYCStatus } = this.props;
    const formData = {
      [key]: data
    };
    updateInstallationKYCStatus(formData);
  };

  onChooseTheme = async () => {
    const { updateInstallationStatus } = this.props;
    updateInstallationStatus('submitted-kyc');
    history.push(routes.newInstallation.chooseTheme());
  };

  onSuccessInstall = async () => {
    const { updateInstallationStatus } = this.props;
    updateInstallationStatus('submitted-kyc');
    history.push(routes.newInstallation.successInstall());
  };

  onSubmitKYC = async (values, isNewUser = false) => {
    await this.onSetStep('features', values);
    if (isNewUser) {
      this.onSuccessInstall();
    } else {
      this.onChooseTheme();
    }
  };

  render() {
    const { currentStepIndex, features, loading } = this.state;
    const { tenantApp } = this.props;
    const isNewUser = checkNewUserSSR(tenantApp);

    console.log('Tenant app: ', { tenantApp, isNewUser });

    return (
      <div className="BC-AnotherLayout BC-page_Installation KYC">
        {currentStepIndex === 1 && (
          <KYCStep1
            loading={loading}
            features={features}
            onSubmitKYC={this.onSubmitKYC}
            isNewUser={isNewUser}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    tenantApp: _.get(state.oauth, 'tenantApp', {}),
    loading: state.install.loading,
    success: state.install.success,
    error: state.install.error,
    KYCValue: state.install.KYCValue,
    installStatus: _.get(state, 'oauth.tenantApp.installStatus'),
    kycData: _.get(state, 'oauth.tenantApp.kycData')
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  addDemographic: body => dispatch(installActions.addDemographic(body)),
  updateInstallationStatus: status => dispatch(installActions.updateInstallationStatus(status)),
  updateInstallationKYCStatus: data => dispatch(installActions.updateInstallationKYCStatus(data))
});

KYCScreen = reduxForm({
  form: 'KYCScreenForm',
  enableReinitialize: true
})(KYCScreen);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(KYCScreen));
